import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Divider, Card, CardContent,
  Tooltip, Stack, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, Edit, Delete, Refresh, Visibility, AutoAwesome,
  Save, Close, Article, Drafts, Public,
  ChevronLeft, ChevronRight, CloudUpload, DeleteOutline,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import MDEditor from '@uiw/react-md-editor';
import { blogApi } from '../services/api';
import { compressImage } from '../utils/imageUtils';

const CATEGORIES = [
  { value: 'guides', label: 'Guides' },
  { value: 'case-studies', label: 'Case Studies' },
  { value: 'product-updates', label: 'Product Updates' },
  { value: 'comparisons', label: 'Comparisons' },
  { value: 'tips', label: 'Tips & Trends' },
];

const EMPTY_POST = {
  title: '', subtitle: '', body: '', author: 'Socialixer Team',
  cover_image: '', tags: [], category: 'guides', meta_description: '',
  status: 'draft', faqs: [], related_slugs: [], ai_generated: false,
};

/* ── Stat Card ──────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, color, theme }) {
  return (
    <Card sx={{ flex: '1 1 200px', background: alpha(color, 0.06), border: `1px solid ${alpha(color, 0.15)}` }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <Box sx={{ color, fontSize: 32 }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ── SEO Preview ────────────────────────────────────────────────────────── */
function SeoPreview({ title, description, slug }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>SEO Preview</Typography>
      <Typography sx={{ color: '#1a0dab', fontSize: 18, fontWeight: 500, lineHeight: 1.3 }}>
        {title || 'Article Title'} | Socialixer Blog
      </Typography>
      <Typography sx={{ color: '#006621', fontSize: 13, mt: 0.25 }}>
        socialixer.com/blog/{slug || 'article-slug'}
      </Typography>
      <Typography sx={{ color: '#545454', fontSize: 13, mt: 0.5, lineHeight: 1.4 }}>
        {description || 'Meta description will appear here...'}
      </Typography>
    </Paper>
  );
}

/* ── FAQ Editor ─────────────────────────────────────────────────────────── */
function FaqEditor({ faqs, onChange }) {
  const addFaq = () => onChange([...faqs, { question: '', answer: '' }]);
  const removeFaq = (i) => onChange(faqs.filter((_, idx) => idx !== i));
  const updateFaq = (i, field, val) => {
    const updated = [...faqs];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" mb={1}>FAQs (for JSON-LD schema)</Typography>
      {faqs.map((faq, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
          <TextField size="small" fullWidth label={`Question ${i + 1}`} value={faq.question}
            onChange={(e) => updateFaq(i, 'question', e.target.value)} sx={{ mb: 1 }} />
          <TextField size="small" fullWidth label="Answer" value={faq.answer} multiline rows={2}
            onChange={(e) => updateFaq(i, 'answer', e.target.value)} />
          <Button size="small" color="error" onClick={() => removeFaq(i)} sx={{ mt: 0.5 }}>Remove</Button>
        </Paper>
      ))}
      <Button size="small" startIcon={<Add />} onClick={addFaq}>Add FAQ</Button>
    </Box>
  );
}

/* ── AI Generate Dialog ─────────────────────────────────────────────────── */
function AiGenerateDialog({ open, onClose, onGenerated }) {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('guides');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional yet approachable');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError('');
    try {
      const result = await blogApi.generate({
        topic: topic.trim(),
        category,
        keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
        tone,
      });
      onGenerated(result);
      onClose();
      setTopic(''); setKeywords('');
    } catch (err) {
      setError(err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Article with AI</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Topic" value={topic} onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., How to grow Instagram followers with AI in 2026" fullWidth />
        <FormControl fullWidth size="small">
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Keywords (comma-separated)" value={keywords}
          onChange={(e) => setKeywords(e.target.value)} placeholder="instagram, followers, AI, automation" fullWidth size="small" />
        <TextField label="Tone" value={tone} onChange={(e) => setTone(e.target.value)} fullWidth size="small" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleGenerate} disabled={loading || !topic.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesome />}>
          {loading ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ── Cover Image Uploader ───────────────────────────────────────────────── */
function CoverImageUploader({ value, onChange }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Track URLs uploaded in this browser session so we can delete them from R2
  // if the user removes the image before saving the post.
  const sessionUploads = useRef(new Set());

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setError('');
    setUploading(true);
    try {
      // If replacing a session-uploaded image, delete the old one from R2 first
      if (value && sessionUploads.current.has(value)) {
        sessionUploads.current.delete(value);
        blogApi.deleteImage(value).catch(() => {}); // best-effort, don't await
      }

      // Compress to max 1280×720 at 85% quality before uploading
      const compressed = await compressImage(file, { maxWidth: 1280, maxHeight: 720, quality: 0.85 });
      const result = await blogApi.uploadImage(compressed);
      sessionUploads.current.add(result.url);
      onChange(result.url);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async () => {
    // If this URL was uploaded in the current session, delete it from R2 immediately
    // (handles "uploaded wrong image then removed without saving")
    if (value && sessionUploads.current.has(value)) {
      sessionUploads.current.delete(value);
      try {
        await blogApi.deleteImage(value);
      } catch (_) {
        // best-effort — don't block the UI
      }
    }
    onChange('');
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Cover Image
      </Typography>

      {value ? (
        <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Box
            component="img"
            src={value}
            alt="Cover"
            sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
          />
          <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
            <Tooltip title="Replace image">
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
              >
                <CloudUpload fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove image">
              <IconButton
                size="small"
                onClick={handleRemove}
                sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(180,0,0,0.7)' } }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: uploading ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 2.5,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: uploading ? 'primary.main' : 'text.secondary' },
          }}
        >
          {uploading ? (
            <Box>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="caption" display="block" color="text.secondary">
                Uploading…
              </Typography>
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                Click to upload cover image
              </Typography>
              <Typography variant="caption" color="text.disabled">
                JPG, PNG, WebP — auto-compressed to 1280×720
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}

/* ── Main AdminBlog Component ───────────────────────────────────────────── */
export default function AdminBlog() {
  const theme = useTheme();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  // Editor state
  const [editing, setEditing] = useState(false);
  const [editSlug, setEditSlug] = useState(null); // null = creating new
  const [form, setForm] = useState({ ...EMPTY_POST });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // AI dialog
  const [aiOpen, setAiOpen] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (statusFilter) params.status = statusFilter;
      const result = await blogApi.list(params);
      setPosts(result.posts || []);
      setTotal(result.total || 0);
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Failed to load posts' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const wordCount = (form.body || '').split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const handleNew = () => {
    setForm({ ...EMPTY_POST });
    setEditSlug(null);
    setTagInput('');
    setEditing(true);
  };

  const handleEdit = async (slug) => {
    try {
      const post = await blogApi.get(slug);
      setForm({
        title: post.title || '', subtitle: post.subtitle || '', body: post.body || '',
        author: post.author || 'Socialixer Team', cover_image: post.cover_image || '',
        tags: post.tags || [], category: post.category || 'guides',
        meta_description: post.meta_description || '', status: post.status || 'draft',
        faqs: post.faqs || [], related_slugs: post.related_slugs || [],
        ai_generated: post.ai_generated || false,
      });
      setTagInput((post.tags || []).join(', '));
      setEditSlug(slug);
      setEditing(true);
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to load post' });
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim() || !form.meta_description.trim()) {
      setMsg({ type: 'error', text: 'Title, body, and meta description are required' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: tagInput.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editSlug) {
        await blogApi.update(editSlug, payload);
        setMsg({ type: 'success', text: 'Post updated' });
      } else {
        const result = await blogApi.create(payload);
        setMsg({ type: 'success', text: `Post created: ${result.slug}` });
      }
      setEditing(false);
      fetchPosts();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await blogApi.delete(slug);
      setMsg({ type: 'success', text: 'Post deleted' });
      fetchPosts();
    } catch (err) {
      setMsg({ type: 'error', text: 'Delete failed' });
    }
  };

  const handleAiGenerated = (result) => {
    setForm(prev => ({
      ...prev,
      title: result.title || prev.title,
      subtitle: result.subtitle || '',
      body: result.body || '',
      meta_description: (result.meta_description || '').slice(0, 160),
      tags: result.tags || [],
      faqs: result.faqs || [],
      ai_generated: true,
    }));
    setTagInput((result.tags || []).join(', '));
    setMsg({ type: 'success', text: 'AI draft generated — review and edit before publishing' });
  };

  const slugPreview = (form.title || '').toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 80);

  // Stats
  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;

  /* ── Editor View ──────────────────────────────────────────────────────── */
  if (editing) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            {editSlug ? 'Edit Post' : 'New Post'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<AutoAwesome />} onClick={() => setAiOpen(true)} variant="outlined" size="small">
              AI Generate
            </Button>
            <Button startIcon={<Close />} onClick={() => setEditing(false)} size="small">Cancel</Button>
          </Stack>
        </Box>

        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left — Editor */}
          <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
            <TextField label="Title" value={form.title} onChange={(e) => updateField('title', e.target.value)}
              fullWidth sx={{ mb: 2 }} />
            <TextField label="Subtitle" value={form.subtitle} onChange={(e) => updateField('subtitle', e.target.value)}
              fullWidth size="small" sx={{ mb: 2 }} />

            <Box data-color-mode="light" sx={{ mb: 2, '& .w-md-editor': { width: '100% !important' } }}>
              <MDEditor value={form.body} onChange={(val) => updateField('body', val || '')} height={500} preview="live" />
            </Box>

            <Stack direction="row" spacing={2} sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
              <span>{wordCount} words</span>
              <span>{readingTime} min read</span>
            </Stack>

            <SeoPreview title={form.title} description={form.meta_description} slug={editSlug || slugPreview} />
          </Box>

          {/* Right — Metadata */}
          <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" mb={2}>Settings</Typography>

              <FormControlLabel
                control={<Switch checked={form.status === 'published'} onChange={(e) => updateField('status', e.target.checked ? 'published' : 'draft')} />}
                label={form.status === 'published' ? 'Published' : 'Draft'}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select value={form.category} label="Category" onChange={(e) => updateField('category', e.target.value)}>
                  {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>

              <TextField label="Tags (comma-separated)" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }} placeholder="instagram, ai-tips, growth" />

              <TextField label="Meta Description" value={form.meta_description}
                onChange={(e) => updateField('meta_description', e.target.value.slice(0, 160))}
                fullWidth size="small" multiline rows={3} sx={{ mb: 1 }}
                placeholder="Brief summary for search engines, e.g. Learn how to grow your Instagram followers using AI-powered content strategies."
                helperText={`${(form.meta_description || '').length}/160 — shown in Google search results`} />

              <TextField label="Author" value={form.author} onChange={(e) => updateField('author', e.target.value)}
                fullWidth size="small" sx={{ mb: 2 }} />

              <CoverImageUploader
                value={form.cover_image}
                onChange={(url) => updateField('cover_image', url)}
              />

              <Divider sx={{ my: 2 }} />
              <FaqEditor faqs={form.faqs} onChange={(faqs) => updateField('faqs', faqs)} />
            </Paper>

            <Button variant="contained" fullWidth startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave} disabled={saving} sx={{ mt: 2 }}>
              {saving ? 'Saving...' : editSlug ? 'Update Post' : 'Create Post'}
            </Button>

            {editSlug && (
              <Button fullWidth startIcon={<Visibility />} component="a"
                href={`/blog/${editSlug}?preview=true`} target="_blank" rel="noopener" sx={{ mt: 1 }}>
                Preview Live
              </Button>
            )}
          </Box>
        </Box>

        <AiGenerateDialog open={aiOpen} onClose={() => setAiOpen(false)} onGenerated={handleAiGenerated} />
      </Box>
    );
  }

  /* ── List View ────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Blog Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Refresh />} onClick={fetchPosts} size="small">Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleNew}>New Post</Button>
        </Stack>
      </Box>

      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard icon={<Article />} label="Total Posts" value={total} color={theme.palette.primary.main} theme={theme} />
        <StatCard icon={<Public />} label="Published" value={publishedCount} color="#22c55e" theme={theme} />
        <StatCard icon={<Drafts />} label="Drafts" value={draftCount} color="#f59e0b" theme={theme} />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Posts table */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : posts.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary" mb={2}>No blog posts yet</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleNew}>Create your first post</Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Read</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post.slug} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={14}>{post.title}</Typography>
                    <Typography variant="caption" color="text.secondary">/blog/{post.slug}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={(post.category || '').replace(/-/g, ' ')} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={post.status} size="small"
                      color={post.status === 'published' ? 'success' : 'default'}
                      variant={post.status === 'published' ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {post.published_at ? new Date(post.published_at).toLocaleDateString() :
                     post.created_at ? new Date(post.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{post.reading_time_min || 1} min</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${post.title}`} onClick={() => handleEdit(post.slug)}><Edit fontSize="small" /></IconButton></Tooltip>
                    {post.status === 'published' && (
                      <Tooltip title="View"><IconButton size="small" aria-label={`View ${post.title}`} component="a" href={`/blog/${post.slug}?preview=true`} target="_blank" rel="noopener"><Visibility fontSize="small" /></IconButton></Tooltip>
                    )}
                    <Tooltip title="Delete"><IconButton size="small" aria-label={`Delete ${post.title}`} color="error" onClick={() => handleDelete(post.slug)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {total > 20 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
          <Button size="small" startIcon={<ChevronLeft />} disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}>
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page} of {Math.ceil(total / 20)}
          </Typography>
          <Button size="small" endIcon={<ChevronRight />} disabled={page >= Math.ceil(total / 20)}
            onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
}
